import React, { useMemo } from "react";
import { View } from "@common/types/views/views";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { buildNestedItems } from "./allocationHelpers";
import { AllocationTable } from "./AllocationTable";
import { RemainingAllocationSection } from "./RemainingAllocationSection";
import { TransactionAllocation } from "./useTransactionAllocation";
import { MatchableItem, MatchItemType, PaymentAmounts } from "../types";
import styles from "../EditAllocationDrawer.module.scss";

interface Props {
    readonly sourceItem: MatchableItem;
    readonly selectedItems: MatchableItem[];
    readonly allocation: TransactionAllocation;
    readonly paymentAmounts: PaymentAmounts;
    readonly onPaymentChange: (
        lineItemId: string,
        value: number,
        maxOpen: number,
    ) => void;
    readonly onRemoveSelectedMatch?: (id: string) => void;
    readonly matchItemType: MatchItemType | null;
    readonly view: View;
    readonly onViewChange: (view: View) => void;
}

export const TransactionAllocationEditor: React.FC<Props> = ({
    sourceItem,
    selectedItems,
    allocation,
    paymentAmounts,
    onPaymentChange,
    onRemoveSelectedMatch,
    matchItemType,
    view,
    onViewChange,
}) => {
    const { remainingState } = allocation;
    const sourceData = useMemo(() => [sourceItem], [sourceItem]);
    const nestedSelectedItems = useMemoByKeys(
        () => buildNestedItems(selectedItems),
        // paymentAmounts included so row references update and trigger
        // ViewTableRowComponent re-renders via its custom comparator.
        [selectedItems, paymentAmounts],
    );

    return (
        <>
            <div>
                <AllocationTable
                    data={sourceData}
                    section="source"
                    matchItemType={matchItemType}
                    view={view}
                    onViewChange={onViewChange}
                    selectedItems={selectedItems}
                />
            </div>

            {selectedItems.length > 0 && (
                <div>
                    <div className={styles.sectionHeader}>Selected matches</div>
                    <AllocationTable
                        data={nestedSelectedItems}
                        section="selected"
                        matchItemType={matchItemType}
                        view={view}
                        onViewChange={onViewChange}
                        paymentAmounts={paymentAmounts}
                        onPaymentChange={onPaymentChange}
                        onRemoveRow={onRemoveSelectedMatch}
                    />
                </div>
            )}

            {allocation.hasAccrualLedger &&
                remainingState.isVisible &&
                remainingState.items.length > 0 && (
                    <RemainingAllocationSection
                        items={remainingState.items}
                        lineHandlers={remainingState}
                        addLineActions={[
                            {
                                key: "add-line",
                                label: "Add line",
                                onClick: remainingState.addLine,
                            },
                        ]}
                        matchItemType={matchItemType}
                        view={view}
                        onViewChange={onViewChange}
                        accounts={allocation.accounts}
                        entityId={allocation.entityId}
                        showAccountNumbers={allocation.showAccountNumbers}
                        amountToBeAllocated={allocation.amountToBeAllocated}
                    />
                )}
        </>
    );
};
